// 9fbef606107a605d69c0edbcd8029e5d
import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import { getBrandInCaps, isGymboree } from '@tcp/core/src/utils';
import Col from '@tcp/core/src/components/common/atoms/Col';
import {
  SIGN_UP_FORM_FONT_FAMILY,
  SIGN_UP_FORM_GETTEXT_LABEL_CLASS,
  SIGN_UP_FORM_TEXT_LABEL_CLASS,
} from '@tcp/core/src/constants/brandSpecific.constants';
import BodyCopy from '@tcp/core/src/components/common/atoms/BodyCopy';
import withStyles from '@tcp/core/src/components/common/hoc/withStyles';
import SignupFormIntroStyle from '@tcp/core/src/components/common/molecules/SignupFormIntro/styles/SignupFormIntro.style';
import {
  GlobalGenericLabels,
  GLOBAL_GENERIC_LABELS,
  lblCurrency,
} from '@tcp/core/src/utils/labels/globalGeneric.labels';
import {
  GLOBAL_EMAIL_SIGN_UP_LABELS,
  GlobalEmailSignUpLabels,
} from '@tcp/core/src/utils/labels/globalEmailSignUp.labels';
import {
  GLOBAL_SMS_SIGN_UP_LABELS,
  GlobalSmsSignUpLabels,
} from '@tcp/core/src/utils/labels/globalSmsSignUp.labels';
import {
  getLabelFromCopyOrFromTranslation,
  renderLabel,
  getDynamicLabelValues,
} from '@tcp/core/src/utils/labels/labelUtilty';

const emailFormType = 'email';
const smsFormType = 'sms';

const globalGenericLabels = getLabelFromCopyOrFromTranslation(
  GLOBAL_GENERIC_LABELS,
  GlobalGenericLabels
);

const emailSignUpLabels = getLabelFromCopyOrFromTranslation(
  GLOBAL_EMAIL_SIGN_UP_LABELS,
  GlobalEmailSignUpLabels
);

const smsSignUpLabels = getLabelFromCopyOrFromTranslation(
  GLOBAL_SMS_SIGN_UP_LABELS,
  GlobalSmsSignUpLabels
);

const getSMSTextLabel = getLabelFromCopyOrFromTranslation(
  `${GLOBAL_SMS_SIGN_UP_LABELS}.lbl_SignUp_getTextLabel`,
  GlobalSmsSignUpLabels
);
const offSMSTextLabel = getLabelFromCopyOrFromTranslation(
  `${GLOBAL_SMS_SIGN_UP_LABELS}.lbl_SignUp_offTextLabel`,
  GlobalSmsSignUpLabels
);

const formLabels = {
  [emailFormType]: {
    staticLabels: emailSignUpLabels,
    dynamicLabels: 'global.emailSignup',
  },
  [smsFormType]: {
    staticLabels: smsSignUpLabels,
    dynamicLabels: 'global.smsSignup',
  },
};

const getFormBasedLabelValue = (formName, labelKey, isDynamicLabel = false) => {
  const { staticLabels } = formLabels[formName] || {};

  if (isDynamicLabel) {
    return getDynamicLabelValues(`dynamic.global_d.${labelKey}`);
  }

  return renderLabel(staticLabels[labelKey], labelKey);
};

const getSubscriptionBasedLabels = (subscriptionType, labelKey, isDynamicLabel = false) => {
  const formType = subscriptionType === emailFormType ? 'email' : 'sms';
  const prefix = subscriptionType === emailFormType ? 'lbl_email_' : 'lbl_sms_';
  const updateLabelKey = isDynamicLabel ? `${prefix}${labelKey}` : labelKey;

  return getFormBasedLabelValue(formType, updateLabelKey, isDynamicLabel);
};

const currencyLabel = renderLabel(globalGenericLabels[lblCurrency], 'lbl_currency');

const combinedThankYouLabelMap = {
  email: 'lbl_combined_signupSms',
  sms: 'lbl_combined_signupEmail',
};

const SignupFormIntro = ({ className, susbscriptionType, noModal, isSMS, fromPage }) => {
  const combinedThankYouLabel = isSMS
    ? smsSignUpLabels[combinedThankYouLabelMap[fromPage]]
    : emailSignUpLabels[combinedThankYouLabelMap[fromPage]];
  const showCombinedThankYouLabel = fromPage && combinedThankYouLabel;
  const isEmailForm = susbscriptionType === emailFormType;
  const offerTypeLabel = getSubscriptionBasedLabels(susbscriptionType, 'lbl_SignUp_offerTypeLabel');
  const getSMSTextLabelValue = !isGymboree()
    ? getSMSTextLabel
    : getDynamicLabelValues('dynamic.global_d.lbl_gym_SignUp_getTextLabel');
  const offSMSTextLabelValue = !isGymboree()
    ? offSMSTextLabel
    : getDynamicLabelValues('dynamic.global_d.lbl_gym_SignUp_offTextLabel');
  const getTextLabel = isEmailForm
    ? getDynamicLabelValues('dynamic.global_d.lbl_SignUp_getTextLabel')
    : getSMSTextLabelValue;
  const offTextLabel = isEmailForm
    ? getDynamicLabelValues('dynamic.global_d.lbl_SignUp_offTextLabel')
    : offSMSTextLabelValue;
  const signUpForLabel = getSubscriptionBasedLabels(susbscriptionType, 'lbl_SignUp_signUpForLabel');
  const offTextLabelSupText = getSubscriptionBasedLabels(
    susbscriptionType,
    'lbl_SignUp_offTextLabel_suptext'
  );
  const nextPurchaseLabel = getSubscriptionBasedLabels(
    susbscriptionType,
    'lbl_SignUp_nextPurchaseLabel'
  );
  const tenTextLabel = getSubscriptionBasedLabels(susbscriptionType, 'SignUp_tenTextLabel', true);

  return (
    <Fragment>
      <Col
        colSize={{ small: 6, medium: 8, large: 12 }}
        ignoreGutter={{ large: true }}
        className={className}
      >
        <section id="sign-up-modal-form-intro-view">
          <BodyCopy
            fontSize={['fs18', 'fs18', 'fs22']}
            fontFamily="secondary"
            textAlign="center"
            className="sign-up__label"
          >
            {signUpForLabel}
          </BodyCopy>
          <BodyCopy
            fontSize={noModal ? 'fs28' : ['fs28', 'fs28', 'fs38']}
            fontFamily="primary"
            fontWeight="black"
            textAlign="center"
            className="offer-type__label"
          >
            {showCombinedThankYouLabel ? combinedThankYouLabel : offerTypeLabel}
          </BodyCopy>
          <BodyCopy fontFamily="primary" className="flash-text" textAlign="center" component="div">
            <BodyCopy
              fontSize="fs25"
              component="span"
              className={SIGN_UP_FORM_GETTEXT_LABEL_CLASS[getBrandInCaps()]}
              color="primary.main"
            >
              {getTextLabel}
            </BodyCopy>
            {!isGymboree() && (
              <>
                <BodyCopy
                  fontSize="fs25"
                  component="span"
                  fontWeight="bold"
                  className="dollar-text"
                  color="primary.main"
                >
                  {currencyLabel}
                </BodyCopy>
                <BodyCopy
                  fontSize="fs10"
                  component="span"
                  fontWeight="bold"
                  className="ten-text"
                  color="primary.main"
                >
                  {tenTextLabel}
                </BodyCopy>
              </>
            )}
            <BodyCopy
              fontSize="fs25"
              textAlign="center"
              className={SIGN_UP_FORM_TEXT_LABEL_CLASS[getBrandInCaps()]}
              color="primary.main"
            >
              {offTextLabel}
              <span className="super-text">
                <BodyCopy fontSize="fs18" component="span" color="primary.main">
                  {offTextLabelSupText}
                </BodyCopy>
              </span>
            </BodyCopy>
          </BodyCopy>
          <BodyCopy
            className="desc-text"
            fontSize={noModal ? ['fs16', 'fs22', 'fs16'] : ['fs16', 'fs18', 'fs16']}
            fontFamily={SIGN_UP_FORM_FONT_FAMILY[getBrandInCaps()]}
            fontWeight="semibold"
            textAlign="center"
          >
            {nextPurchaseLabel}
          </BodyCopy>
        </section>
      </Col>
    </Fragment>
  );
};

SignupFormIntro.propTypes = {
  className: PropTypes.string,
  noModal: PropTypes.bool,
  isSMS: PropTypes.bool,
  fromPage: PropTypes.string,
  susbscriptionType: PropTypes.string.isRequired,
};

SignupFormIntro.defaultProps = {
  className: '',
  noModal: false,
  isSMS: false,
  fromPage: '',
};

export { SignupFormIntro as SignupFormIntroVanilla };
export default withStyles(SignupFormIntro, SignupFormIntroStyle);
